import React, { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { SprintDetail, SprintStatus } from '../../../../../lib/functions/sprint'
import {
  ProductBacklogItemRow,
  SprintProductItemDetail,
} from '../../productBacklog'
import { WorkloadUnit } from '../../../../../lib/functions/workload'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useWorkloadUnit } from '../../../../hooks/useWorkloadUnit'
import { SprintAdjustmentItem, ViewMode } from './types'
import { WorkloadModeView } from './WorkloadModeView'
import { SelectionModeView } from './SelectionModeView'

// 型のエクスポート
export type { SprintAdjustmentItem } from './types'

interface Props {
  open: boolean
  onClose: () => void
  onApply: (items: SprintAdjustmentItem[]) => void
  sprints: SprintDetail[]
  deliverable: ProductBacklogItemRow
  teamUuid: string | undefined
  workloadUnit: WorkloadUnit
}

const isValidSprint = (status: SprintStatus): boolean => {
  return status === SprintStatus.STANDBY || status === SprintStatus.INPROGRESS
}

export const SprintAssignmentDialog: React.FC<Props> = ({
  open,
  onClose,
  onApply,
  sprints,
  deliverable,
  teamUuid,
  workloadUnit,
}) => {
  const intl = useIntl()
  const [items, setItems] = useState<SprintAdjustmentItem[]>([])
  const [viewMode, setViewMode] = useState<ViewMode>('workload')
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)

  // ワークロード単位の変換
  const { hoursPerSelectedUnit, convertWorkloadFromHourToSelectedUnit } =
    useWorkloadUnit(workloadUnit)

  // 選択単位から時間への変換
  const convertWorkloadFromSelectedUnitToHour = useCallback(
    (value: number) => value * hoursPerSelectedUnit,
    [hoursPerSelectedUnit]
  )

  // 単位ラベルの取得
  const workloadUnitLabel = useMemo(() => {
    switch (workloadUnit) {
      case WorkloadUnit.MONTH:
        return intl.formatMessage({ id: 'workloadUnit.month' })
      case WorkloadUnit.DAY:
        return intl.formatMessage({ id: 'workloadUnit.day' })
      case WorkloadUnit.HOUR:
      default:
        return intl.formatMessage({ id: 'workloadUnit.hour' })
    }
  }, [workloadUnit, intl])

  // ダイアログが開くたびに状態を初期化
  useEffect(() => {
    if (!open) return

    // スプリント一覧から編集用データを構築
    const currentSprintItems: SprintProductItemDetail[] =
      deliverable.sprintProductItems || []
    const currentSprintMap = new Map(
      currentSprintItems.map(spi => [spi.sprintUuid, spi])
    )
    const linkedSprintUuids = new Set(
      currentSprintItems.map(spi => spi.sprintUuid)
    )

    // チームでフィルタ、num昇順でソート
    // 紐づいているスプリントは常に表示、未紐づけは有効なステータスのみ
    const filteredSprints = sprints
      .filter(
        s =>
          s.teamUuid === teamUuid &&
          (linkedSprintUuids.has(s.uuid) || isValidSprint(s.status))
      )
      .sort((a, b) => a.num - b.num)

    const adjustmentItems: SprintAdjustmentItem[] = filteredSprints.map(
      sprint => {
        const existing = currentSprintMap.get(sprint.uuid)
        const isChecked = !!existing
        const estimatedHour = existing?.estimatedHour || 0

        return {
          sprintUuid: sprint.uuid,
          sprintName: sprint.name,
          startDate: sprint.startDate,
          endDate: sprint.endDate,
          checked: isChecked,
          estimatedHour,
        }
      }
    )

    setItems(adjustmentItems)
    setViewMode('workload')
    setAddMenuAnchor(null)
  }, [open, deliverable, sprints, teamUuid])

  // 紐づいているスプリントのみ（工数入力モード用）
  const linkedItems = useMemo(() => items.filter(item => item.checked), [items])

  // 見積工数の合計（時間単位）
  const totalEstimatedHour = useMemo(
    () => linkedItems.reduce((sum, item) => sum + item.estimatedHour, 0),
    [linkedItems]
  )

  // 未紐づけスプリント（ドロップダウン用）
  const unlinkedItems = useMemo(
    () => items.filter(item => !item.checked),
    [items]
  )

  // スプリント追加メニュー
  const handleAddMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAddMenuAnchor(event.currentTarget)
    },
    []
  )

  const handleAddMenuClose = useCallback(() => {
    setAddMenuAnchor(null)
  }, [])

  const handleAddSprint = useCallback((sprintUuid: string) => {
    setItems(prev =>
      prev.map(item =>
        item.sprintUuid === sprintUuid ? { ...item, checked: true } : item
      )
    )
    setAddMenuAnchor(null)
  }, [])

  // スプリント削除（工数入力モード用）
  const handleRemoveSprint = useCallback((sprintUuid: string) => {
    setItems(prev =>
      prev.map(item =>
        item.sprintUuid === sprintUuid
          ? { ...item, checked: false, estimatedHour: 0 }
          : item
      )
    )
  }, [])

  const handleCheckChange = useCallback(
    (sprintUuid: string, checked: boolean) => {
      setItems(prev =>
        prev.map(item =>
          item.sprintUuid === sprintUuid
            ? {
                ...item,
                checked,
                estimatedHour: checked ? item.estimatedHour : 0,
              }
            : item
        )
      )
    },
    []
  )

  const handleWorkloadChange = useCallback(
    (sprintUuid: string, value: string) => {
      const numValue = parseFloat(value) || 0
      // 選択単位で入力された値を時間に変換して保存
      const hourValue = convertWorkloadFromSelectedUnitToHour(numValue)
      setItems(prev =>
        prev.map(item =>
          item.sprintUuid === sprintUuid
            ? { ...item, estimatedHour: hourValue }
            : item
        )
      )
    },
    [convertWorkloadFromSelectedUnitToHour]
  )

  const handleApply = useCallback(() => {
    onApply(items)
  }, [items, onApply])

  // 日付フォーマット
  const formatDate = useCallback((dateStr: string) => {
    if (!dateStr) return ''
    return dateV2Service.formatMonthAndDate(
      dateV2Service.newDateFromString(dateStr)
    )
  }, [])

  // 変更があるかどうかを検出
  const hasChanges = useMemo(() => {
    const currentSprintItems: SprintProductItemDetail[] =
      deliverable.sprintProductItems || []
    const currentSprintMap = new Map(
      currentSprintItems.map(spi => [spi.sprintUuid, spi])
    )

    return items.some(item => {
      const original = currentSprintMap.get(item.sprintUuid)
      const originalChecked = !!original
      const originalHour = original?.estimatedHour || 0

      return (
        item.checked !== originalChecked ||
        (item.checked && item.estimatedHour !== originalHour)
      )
    })
  }, [items, deliverable.sprintProductItems])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        {intl.formatMessage({
          id: 'productBacklog.sprintInteractiveAdjustment.title',
        })}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {/* 固定ヘッダー部分: 2行レイアウト */}
        <Box
          sx={{
            px: 2,
            py: 1,
            flexShrink: 0,
          }}
        >
          {/* 1行目: 成果物 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ flexShrink: 0 }}
            >
              {intl.formatMessage({
                id: 'productBacklog.sprintInteractiveAdjustment.deliverable',
              })}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', color: '#000' }}
              noWrap={true}
              title={deliverable.wbsItem.displayName || '-'}
            >
              {deliverable.wbsItem.displayName || '-'}
            </Typography>
          </Box>
          {/* 2行目: 担当者 + 見積合計 */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="body1" color="textSecondary">
              {intl.formatMessage({
                id: 'productBacklog.sprintInteractiveAdjustment.assignee',
              })}
            </Typography>
            {deliverable.wbsItem.responsible ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Avatar
                  src={deliverable.wbsItem.responsible.iconUrl}
                  sx={{ width: 24, height: 24 }}
                />
                <Typography variant="h6" noWrap={true} sx={{ color: '#000' }}>
                  {deliverable.wbsItem.responsible.name}
                </Typography>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ color: '#000' }}>
                -
              </Typography>
            )}
            {/* 元の見積と見積合計（右寄せ、上下配置） */}
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0.5,
              }}
            >
              {/* 元の見積 */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage({
                    id: 'productBacklog.sprintInteractiveAdjustment.originalEstimate',
                  })}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#000', minWidth: 60, textAlign: 'right' }}
                >
                  {convertWorkloadFromHourToSelectedUnit(
                    deliverable.wbsItem.estimatedWorkload?.hour || 0
                  )}{' '}
                  {workloadUnitLabel}
                </Typography>
              </Box>
              {/* 見積合計 */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage({
                    id: 'productBacklog.sprintInteractiveAdjustment.total',
                  })}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#000', minWidth: 60, textAlign: 'right' }}
                >
                  {convertWorkloadFromHourToSelectedUnit(totalEstimatedHour)}{' '}
                  {workloadUnitLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* モードに応じた表示切り替え */}
        {viewMode === 'workload' ? (
          <WorkloadModeView
            linkedItems={linkedItems}
            unlinkedItems={unlinkedItems}
            workloadUnitLabel={workloadUnitLabel}
            addMenuAnchor={addMenuAnchor}
            onAddMenuOpen={handleAddMenuOpen}
            onAddMenuClose={handleAddMenuClose}
            onAddSprint={handleAddSprint}
            onRemoveSprint={handleRemoveSprint}
            onWorkloadChange={handleWorkloadChange}
            onSwitchToSelection={() => setViewMode('selection')}
            formatDate={formatDate}
            convertWorkloadFromHourToSelectedUnit={
              convertWorkloadFromHourToSelectedUnit
            }
          />
        ) : (
          <SelectionModeView
            items={items}
            onCheckChange={handleCheckChange}
            onSwitchToWorkload={() => setViewMode('workload')}
            formatDate={formatDate}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          disabled={!hasChanges}
          sx={{ px: 1.5 }}
        >
          {intl.formatMessage({
            id: 'productBacklog.sprintInteractiveAdjustment.apply',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
